import React, { useEffect } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Spin,
  Card,
  Space,
  Statistic,
  Row,
  Col,
  Button,
  Divider,
  Table,
  Timeline,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  ShoppingCartOutlined,
  BankOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useRequisition } from "../../../hooks/finance/useRequisition";
import Utilities from "../../../dependencies/custom/react-utilities";

const RequisitionDetailDrawer = ({
  visible,
  onClose,
  requisitionId,
  onSubmit,
  onHodApprove,
  onFinanceApprove,
  onMarkFulfilled,
}) => {
  const { requisitionDetails, loading, fetchRequisitionDetails } =
    useRequisition();

  const downloadAttachment = async (attachment) => {
    try {
      const response = await fetch(attachment.downloadUrl, {
        headers: {
          Authorization: `Bearer ${Utilities.getCookie("access")}`,
        },
      });
      if (!response.ok) throw new Error("Download failed");
      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.originalName || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      message.error("Could not download the attachment");
    }
  };

  useEffect(() => {
    if (visible && requisitionId) {
      fetchRequisitionDetails(requisitionId);
    }
  }, [visible, requisitionId]);

  const getStatusColor = (status) => {
    const statusColors = {
      Draft: "default",
      Submitted: "blue",
      "HoD Approved": "cyan",
      "HoD Rejected": "red",
      "Finance Approved": "purple",
      "Finance Rejected": "red",
      Procurement: "orange",
      Fulfilled: "green",
      Cancelled: "gray",
    };
    return statusColors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: "default",
      Medium: "blue",
      High: "orange",
      Urgent: "red",
    };
    return colors[priority] || "default";
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatFileSize = (bytes) => {
    const size = Number(bytes || 0);
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const itemColumns = [
    {
      title: "Item",
      dataIndex: "itemName",
      key: "itemName",
      width: 180,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 70,
      align: "center",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      width: 80,
    },
    {
      title: "Est. Price",
      dataIndex: "estimatedUnitPrice",
      key: "estimatedUnitPrice",
      width: 100,
      align: "right",
      render: (price) => formatCurrency(price),
    },
    {
      title: "Total",
      key: "total",
      width: 120,
      align: "right",
      render: (_, record) => (
        <strong>
          {formatCurrency(
            (record.quantity || 0) * (record.estimatedUnitPrice || 0),
          )}
        </strong>
      ),
    },
  ];

  // Build approval timeline
  const getTimelineItems = () => {
    if (!requisitionDetails) return [];

    const items = [
      {
        color: "green",
        children: (
          <>
            <p>
              <strong>Created</strong>
            </p>
            <p>
              {requisitionDetails.requestedByName} -{" "}
              {dayjs(requisitionDetails.createdAt).format("DD MMM YYYY HH:mm")}
            </p>
          </>
        ),
      },
    ];

    if (requisitionDetails.hodApprovedAt) {
      items.push({
        color: requisitionDetails.status === "HoD Rejected" ? "red" : "green",
        children: (
          <>
            <p>
              <strong>
                {requisitionDetails.status === "HoD Rejected"
                  ? "HoD Rejected"
                  : "HoD Approved"}
              </strong>
            </p>
            <p>
              {requisitionDetails.hodApprovedByName} -{" "}
              {dayjs(requisitionDetails.hodApprovedAt).format(
                "DD MMM YYYY HH:mm",
              )}
            </p>
            {requisitionDetails.hodRejectionReason && (
              <p style={{ color: "#ff4d4f" }}>
                Reason: {requisitionDetails.hodRejectionReason}
              </p>
            )}
          </>
        ),
      });
    }

    if (requisitionDetails.financeApprovedAt) {
      items.push({
        color:
          requisitionDetails.status === "Finance Rejected" ? "red" : "green",
        children: (
          <>
            <p>
              <strong>
                {requisitionDetails.status === "Finance Rejected"
                  ? "Finance Rejected"
                  : "Finance Approved"}
              </strong>
            </p>
            <p>
              {requisitionDetails.financeApprovedByName} -{" "}
              {dayjs(requisitionDetails.financeApprovedAt).format(
                "DD MMM YYYY HH:mm",
              )}
            </p>
            {requisitionDetails.financeRejectionReason && (
              <p style={{ color: "#ff4d4f" }}>
                Reason: {requisitionDetails.financeRejectionReason}
              </p>
            )}
          </>
        ),
      });
    }

    if (requisitionDetails.fulfilledAt) {
      items.push({
        color: "green",
        children: (
          <>
            <p>
              <strong>Fulfilled</strong>
            </p>
            <p>
              {requisitionDetails.fulfilledByName} -{" "}
              {dayjs(requisitionDetails.fulfilledAt).format(
                "DD MMM YYYY HH:mm",
              )}
            </p>
          </>
        ),
      });
    }

    return items;
  };

  if (!requisitionDetails && !loading) {
    return null;
  }

  return (
    <Drawer
      title={
        <Space>
          <ShoppingCartOutlined />
          <span>Requisition Details</span>
          {requisitionDetails && (
            <Tag color={getStatusColor(requisitionDetails.status)}>
              {requisitionDetails.status}
            </Tag>
          )}
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={800}
      extra={
        requisitionDetails && (
          <Space>
            {requisitionDetails.status === "Draft" && (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => {
                  onSubmit(requisitionDetails);
                  onClose();
                }}
              >
                Submit
              </Button>
            )}
            {requisitionDetails.status === "Submitted" && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    onHodApprove(requisitionDetails, "approve");
                    onClose();
                  }}
                >
                  HoD Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    onHodApprove(requisitionDetails, "reject");
                    onClose();
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            {requisitionDetails.status === "HoD Approved" && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={() => {
                    onFinanceApprove(requisitionDetails, "approve");
                    onClose();
                  }}
                >
                  Finance Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => {
                    onFinanceApprove(requisitionDetails, "reject");
                    onClose();
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            {(requisitionDetails.status === "Finance Approved" ||
              requisitionDetails.status === "Procurement") && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  onMarkFulfilled(requisitionDetails);
                  onClose();
                }}
              >
                Mark Fulfilled
              </Button>
            )}
          </Space>
        )
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        requisitionDetails && (
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Summary */}
            <Card title="Summary" size="small">
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Total Amount"
                    value={formatCurrency(requisitionDetails.totalAmount)}
                    prefix="UGX"
                    valueStyle={{ color: "#1890ff" }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Items Count"
                    value={requisitionDetails.items?.length || 0}
                  />
                </Col>
              </Row>
            </Card>

            {/* Budget Allocation */}
            <Card
              title={
                <Space>
                  <BankOutlined />
                  <span>Budget Allocation</span>
                </Space>
              }
              size="small"
            >
              {requisitionDetails.budgetLineItemId ? (
                <Descriptions column={2} bordered size="small">
                  <Descriptions.Item label="Line Item">
                    <Tag color="blue">
                      {requisitionDetails.budgetLineItemCategory || "-"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Budget Fiscal Year">
                    {requisitionDetails.budgetFiscalYear || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Requisition Amount" span={2}>
                    <strong style={{ color: "#1890ff" }}>
                      UGX {formatCurrency(requisitionDetails.totalAmount)}
                    </strong>
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <div
                  style={{
                    color: "#8c8c8c",
                    fontStyle: "italic",
                    padding: "8px 0",
                  }}
                >
                  No budget line item linked to this requisition.
                </div>
              )}
            </Card>

            {/* Details */}
            <Card title="Requisition Information" size="small">
              <Descriptions column={2} bordered size="small">
                <Descriptions.Item label="Requisition #">
                  <strong>{requisitionDetails.requisitionNumber}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Item">
                  {requisitionDetails.departmentName}
                </Descriptions.Item>
                <Descriptions.Item label="Title" span={2}>
                  {requisitionDetails.title}
                </Descriptions.Item>
                <Descriptions.Item label="Priority">
                  <Tag color={getPriorityColor(requisitionDetails.priority)}>
                    {requisitionDetails.priority}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={getStatusColor(requisitionDetails.status)}>
                    {requisitionDetails.status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Requested By">
                  {requisitionDetails.requestedByName}
                </Descriptions.Item>
                <Descriptions.Item label="Needed By">
                  {requisitionDetails.neededByDate
                    ? dayjs(requisitionDetails.neededByDate).format(
                        "DD MMM YYYY",
                      )
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Justification" span={2}>
                  {requisitionDetails.justification}
                </Descriptions.Item>
                {requisitionDetails.description && (
                  <Descriptions.Item label="Notes" span={2}>
                    {requisitionDetails.description}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            <Card
              title={`Attachments (${requisitionDetails.attachments?.length || 0})`}
              size="small"
            >
              {requisitionDetails.attachments?.length ? (
                <Space direction="vertical" style={{ width: "100%" }}>
                  {requisitionDetails.attachments.map((attachment) => (
                    <Button
                      key={attachment.attachmentId}
                      type="link"
                      style={{ padding: 0, height: "auto", textAlign: "left" }}
                      onClick={() => downloadAttachment(attachment)}
                    >
                      <PaperClipOutlined />{" "}
                      {attachment.originalName || "Attachment"}{" "}
                      <span style={{ color: "#8c8c8c" }}>
                        ({formatFileSize(attachment.fileSize)})
                      </span>
                    </Button>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#8c8c8c" }}>No attachments</span>
              )}
            </Card>

            {/* Items */}
            <Card title="Requested Items" size="small">
              <Table
                columns={itemColumns}
                dataSource={requisitionDetails.items || []}
                rowKey="itemId"
                size="small"
                pagination={false}
                scroll={{ x: 600 }}
              />
            </Card>

            <Divider />

            {/* Approval Timeline */}
            <Card title="Approval Timeline" size="small">
              <Timeline items={getTimelineItems()} />
            </Card>
          </Space>
        )
      )}
    </Drawer>
  );
};

export default RequisitionDetailDrawer;
